import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  useTheme,
  Pagination,
  Stack,
} from '@mui/material';
import {
  Send as SendIcon,
  Archive as ArchiveIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Reply as ReplyIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
// Using dangerouslySetInnerHTML for HTML rendering (sanitized)

interface Email {
  _id: string;
  direction: 'sent' | 'received';
  from: {
    email: string;
    name?: string;
  };
  to: Array<{
    email: string;
    name?: string;
  }>;
  subject: string;
  body: string;
  bodyHtml?: string;
  createdAt: string;
  read?: boolean;
  archived?: boolean;
  leadId?: string;
  lead?: {
    originalData: any;
  };
  sentBy?: {
    username: string;
    clientName: string;
    email: string;
  };
  status?: string;
  opened?: boolean;
  clicked?: boolean;
  replied?: boolean;
  messageId?: string;
  threadId?: string;
}

interface MailboxStats {
  totalSent: number;
}

const CRMMailbox: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [emails, setEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [stats, setStats] = useState<MailboxStats>({
    totalSent: 0,
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [archived, setArchived] = useState(false);

  const direction = 'sent'; // Only show sent emails
  const limit = 25;

  // Fetch mailbox stats
  const fetchStats = async () => {
    try {
      const response = await api.get('/api/crm/mailbox/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching mailbox stats:', error);
    }
  };

  // Fetch emails
  const fetchEmails = async () => {
    setLoading(true);
    try {
      const params: any = {
        direction,
        page,
        limit,
        archived,
      };
      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await api.get('/api/crm/mailbox', { params });
      if (response.data.success) {
        setEmails(response.data.data);
        setTotalPages(response.data.pagination.pages);
      }
    } catch (error) {
      console.error('Error fetching emails:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchEmails();
  }, [page, searchQuery, archived]);

  const handleEmailClick = async (email: Email) => {
    setSelectedEmail(email);
    setEmailDialogOpen(true);
  };

  const handleArchive = async (emailId: string, currentArchived: boolean) => {
    try {
      await api.put(`/api/crm/mailbox/${emailId}/archive`, { archived: !currentArchived });
      setEmails(emails.filter(e => e._id !== emailId));
      fetchStats();
      if (selectedEmail?._id === emailId) {
        setEmailDialogOpen(false);
        setSelectedEmail(null);
      }
    } catch (error) {
      console.error('Error archiving email:', error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
    }
  };

  const getLeadName = (email: Email) => {
    if (email.lead?.originalData) {
      const firstName = email.lead.originalData['First Name'] || email.lead.originalData['firstName'];
      const lastName = email.lead.originalData['Last Name'] || email.lead.originalData['lastName'];
      const companyName = email.lead.originalData['Company Name'] || email.lead.originalData['companyName'];
      
      if (firstName || lastName) {
        return `${firstName || ''} ${lastName || ''}`.trim();
      }
      return companyName || email.direction === 'sent' ? email.to[0]?.email : email.from.email;
    }
    return email.direction === 'sent' ? email.to[0]?.email : email.from.email;
  };

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Mailbox</Typography>
        <Stack direction="row" spacing={2}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchEmails();
              fetchStats();
            }}
            variant="outlined"
          >
            Refresh
          </Button>
          <Button
            startIcon={<ArchiveIcon />}
            onClick={() => setArchived(!archived)}
            variant={archived ? 'contained' : 'outlined'}
          >
            {archived ? 'Show Active' : 'Show Archived'}
          </Button>
        </Stack>
      </Box>

      {/* Stats */}
      <Box display="flex" gap={2} mb={3}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <SendIcon color="primary" />
            <Box>
              <Typography variant="h6">{stats.totalSent}</Typography>
              <Typography variant="caption" color="text.secondary">
                Total Sent
              </Typography>
            </Box>
          </Box>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon color="action" />
            <Box>
              <Typography variant="h6">{emails.length}</Typography>
              <Typography variant="caption" color="text.secondary">
                {archived ? 'Archived' : 'Active'} Emails
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        placeholder="Search emails..."
        value={searchQuery}
        onChange={(e) => {
          setSearchQuery(e.target.value);
          setPage(1);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: searchQuery && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearchQuery('')}>
                ×
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {/* Email List */}
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
          </Box>
        ) : emails.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={4} flex={1}>
            <EmailIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No sent emails found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {archived ? 'Try switching to active emails' : 'Sent emails will appear here'}
            </Typography>
          </Box>
        ) : (
          <>
            <List sx={{ flex: 1, overflow: 'auto' }}>
              {emails.map((email, index) => (
                <React.Fragment key={email._id}>
                  <ListItem
                    button
                    onClick={() => handleEmailClick(email)}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemIcon>
                      <SendIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1">
                            {getLeadName(email)}
                          </Typography>
                          {email.opened && (
                            <Chip label="Opened" size="small" color="success" />
                          )}
                          {email.clicked && (
                            <Chip label="Clicked" size="small" color="info" />
                          )}
                          {email.replied && email.direction === 'sent' && (
                            <Chip label="Replied" size="small" color="primary" />
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.primary">
                            {email.subject}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(email.createdAt)}
                          </Typography>
                        </>
                      }
                    />
                    <Box>
                      <Tooltip title={email.archived ? 'Unarchive' : 'Archive'}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArchive(email._id, email.archived || false);
                          }}
                        >
                          <ArchiveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItem>
                  {index < emails.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" p={2}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_event, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Email Detail Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedEmail && (
          <>
            <DialogTitle>
              <Box>
                <Typography variant="h6">{selectedEmail.subject}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {`To: ${getLeadName(selectedEmail)} <${selectedEmail.to[0]?.email || ''}>`}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {formatDate(selectedEmail.createdAt)}
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                {selectedEmail.opened && (
                  <Chip label="Opened" size="small" color="success" sx={{ mr: 1 }} />
                )}
                {selectedEmail.clicked && (
                  <Chip label="Clicked" size="small" color="info" sx={{ mr: 1 }} />
                )}
                {selectedEmail.replied && selectedEmail.direction === 'sent' && (
                  <Chip label="Replied" size="small" color="primary" />
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{
                  '& img': { maxWidth: '100%' },
                  '& a': { color: 'primary.main' },
                }}
              >
                {selectedEmail.bodyHtml ? (
                  <div dangerouslySetInnerHTML={{ __html: selectedEmail.bodyHtml }} />
                ) : (
                  <Typography variant="body1" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                    {selectedEmail.body}
                  </Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => handleArchive(selectedEmail._id, selectedEmail.archived || false)}>
                {selectedEmail.archived ? 'Unarchive' : 'Archive'}
              </Button>
              {selectedEmail.leadId && (
                <Button
                  variant="contained"
                  startIcon={<ReplyIcon />}
                  onClick={() => {
                    setEmailDialogOpen(false);
                    // Navigate to CRM with lead selected
                    window.location.href = `/client/${user?.username}/crm?leadId=${selectedEmail.leadId}`;
                  }}
                >
                  View Lead
                </Button>
              )}
              <Button onClick={() => setEmailDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default CRMMailbox;

